import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useLanguage, productsCopy } from "../i18n";

const IMGS = [
  "https://images.unsplash.com/photo-1734894966816-9f2b7bf9c605?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1526438281712-35d96b8586e5?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1743071877775-87d87793c8b4?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1642714772772-c61bc3913e3a?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1471194402529-8e0f5a675de6?q=80&w=1200&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1537640538966-79f369143f8f?q=80&w=1200&auto=format&fit=crop",
];

const SEASONS = [
  { months: [10, 11, 0, 1, 2, 3, 4], peak: [11, 0, 1, 2] },
  { months: [8, 9, 10, 11, 0, 1, 2], peak: [9, 10, 11, 0] },
  { months: [9, 10, 11, 0, 1], peak: [10, 11, 0] },
  { months: [8, 9, 10, 11], peak: [9, 10] },
  { months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], peak: [] },
  { months: [6, 7, 8, 9], peak: [7, 8] },
];

const TESTIDS = [
  "product-card-orange",
  "product-card-lemon",
  "product-card-mandarin",
  "product-card-pomegranate",
  "product-card-tomato",
  "product-card-grape",
];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export const ProductsCatalog = () => {
  const { lang } = useLanguage();
  const c = productsCopy[lang] || productsCopy.tr;
  const nowMonth = new Date().getMonth();

  return (
    <section id="urunler" data-testid="products-section" className="catalog-section relative overflow-hidden py-28 sm:py-36">
      <div className="catalog-aura" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.p {...reveal} className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#48e38e]">
            {c.overline}
          </motion.p>
          <motion.h2
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.08 }}
            className="font-display mt-4 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl"
            data-testid="products-title"
          >
            {c.title}
          </motion.h2>
          <motion.p
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.15 }}
            className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
          >
            {c.sub}
          </motion.p>
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.22 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500"
            data-testid="products-legend"
          >
            <span className="flex items-center gap-2.5">
              <span className="season-bar is-season w-6 flex-none" />
              {c.legendSeason}
            </span>
            <span className="flex items-center gap-2.5">
              <span className="season-bar is-peak w-6 flex-none" />
              {c.legendPeak}
            </span>
            <span className="flex items-center gap-2.5">
              <span className="season-bar is-now w-6 flex-none" />
              {c.legendNow}
            </span>
          </motion.div>
        </div>

        <div className="mt-16 grid gap-6 sm:mt-20 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {c.items.map((item, i) => {
            const s = SEASONS[i];
            const inSeason = s.months.includes(nowMonth);
            return (
              <motion.div key={TESTIDS[i]} {...reveal} transition={{ ...reveal.transition, delay: 0.07 * i }}>
                <article data-testid={TESTIDS[i]} data-cursor className="catalog-card group h-full">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={IMGS[i]}
                      alt={item.name}
                      loading="lazy"
                      className="catalog-img h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a10] via-transparent to-transparent" />
                    {inSeason && (
                      <span
                        data-testid={`${TESTIDS[i]}-in-season`}
                        className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-[#48e38e]/40 bg-[#04120b]/70 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#48e38e] backdrop-blur-md"
                      >
                        <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#48e38e]" />
                        {c.inSeason}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6 sm:p-7">
                    <h3 className="font-display text-lg font-semibold text-white">{item.name}</h3>
                    <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#48e38e]/80">
                      <MapPin size={11} />
                      {item.origin}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{item.desc}</p>
                    <div className="mt-auto pt-7">
                      <div className="flex gap-[3px] pt-1.5" data-testid={`${TESTIDS[i]}-calendar`}>
                        {c.months.map((_, m) => {
                          const inM = s.months.includes(m);
                          const isPeak = s.peak.includes(m);
                          const isNow = m === nowMonth;
                          return (
                            <div
                              key={m}
                              title={c.months[m]}
                              className={`season-bar ${isPeak ? "is-peak" : inM ? "is-season" : ""} ${isNow ? "is-now" : ""}`}
                            />
                          );
                        })}
                      </div>
                      <div className="mt-2.5 flex justify-between font-mono text-[9px] uppercase tracking-wider text-slate-600">
                        <span>{c.months[0]}</span>
                        <span>{c.months[5]}</span>
                        <span>{c.months[11]}</span>
                      </div>
                    </div>
                  </div>
                </article>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          {...reveal}
          className="mt-16 flex flex-col items-center gap-5 text-center sm:mt-20"
          data-testid="products-range-note"
        >
          <a
            href="#iletisim"
            data-testid="products-range-cta"
            data-cursor
            className="btn-liquid inline-flex items-center gap-2.5 rounded-full border border-[#48e38e]/35 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#48e38e]"
          >
            {c.rangeCta}
            <ArrowUpRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
